import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useClient } from '../../../contexts/ClientContext';
import { FiUsers as UsersIcon, FiUserPlus as UserPlus, FiSearch as Search } from 'react-icons/fi';
import { Card, Button } from '../../../components/ui';
import { apiCall } from '../../../services/realApi';

interface PlatformUser {
  id: string;
  name?: string;
  email?: string;
  role?: string;
  job_title?: string;
  status?: string;
}

const Users = () => {
  const { user } = useAuth();
  const { selectedClientId, isAWSUser } = useClient();
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      let endpoint = '/users';
      // For AWS users, client_id is required
      if (isAWSUser) {
        if (!selectedClientId) {
          setUsers([]);
          setLoading(false);
          return;
        }
        endpoint += `?client_id=${selectedClientId}`;
      }
      const data = await apiCall(endpoint);
      setUsers(data || []);
    } catch (err: unknown) {
      console.error('Error loading users:', err);
      const apiErr = err as { status?: number; message?: string };
      if (apiErr.status === 403) {
        setError("You don't have access to this client");
      } else {
        setError(apiErr.message || 'Failed to load users');
      }
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadUsers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedClientId]);

  const filteredUsers = users.filter((u) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      u.name?.toLowerCase().includes(searchLower) ||
      u.email?.toLowerCase().includes(searchLower) ||
      u.role?.toLowerCase().includes(searchLower)
    );
  });

  const getRoleBadgeColor = (role?: string): string => {
    switch (role?.toLowerCase()) {
      case 'aws': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case 'partner': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'client': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300';
      case 'aivilo_staff': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStatusBadgeColor = (status?: string): string => {
    return ['ACTIVE', 'active'].includes(status ?? '')
      ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
      : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
  };

  // Show empty state for AWS users when no client is selected
  if (isAWSUser && !selectedClientId) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">
            Users
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400 transition-colors">
            Manage and view all users in the system
          </p>
        </div>
        <Card>
          <div className="p-12 text-center">
            <UsersIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Please select a client to view resources
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              Use the client selector in the navigation bar to choose a client.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">
              Users
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400 transition-colors">
              Manage and view all users in the system
            </p>
          </div>
          <Button
            variant="primary"
            onClick={() => {
              // TODO: Implement create user modal
              alert('Create user functionality coming soon');
            }}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card className="mb-6">
        <div className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search users by name, email, or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            />
          </div>
        </div>
      </Card>

      {/* Error State */}
      {error && (
        <Card className="mb-6">
          <div className="p-6 text-center">
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <Button
              variant="secondary"
              className="mt-4"
              onClick={loadUsers}
            >
              Retry
            </Button>
          </div>
        </Card>
      )}

      {/* Users Table */}
      <Card>
        {filteredUsers.length === 0 ? (
          <div className="p-6 text-center">
            <UsersIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              {searchTerm ? 'No users found matching your search' : 'No users found'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Job Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredUsers.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {u.name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {u.email || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(u.role)}`}>
                        {u.role || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {u.job_title ? (
                        <span className="capitalize">
                          {u.job_title.replaceAll('_', ' ').replaceAll(/\b\w/g, (l) => l.toUpperCase())}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(u.status)}`}>
                        {u.status || 'UNKNOWN'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Stats */}
      {filteredUsers.length > 0 && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <div className="p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Users</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{filteredUsers.length}</p>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Active Users</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {filteredUsers.filter((u) => ['ACTIVE', 'active'].includes(u.status ?? "")).length}
              </p>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">AWS Users</p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {filteredUsers.filter((u) => u.role?.toLowerCase() === 'aws').length}
              </p>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Partners</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {filteredUsers.filter((u) => u.role?.toLowerCase() === 'partner').length}
              </p>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Users;
